import {
  HttpInterceptor,
  HttpErrorResponse,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { SharedService } from '@services/shared.service';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  authReq: any;
  sharedService = inject(SharedService);

  private static isSessionExpired = false;

  constructor(private router: Router) {}

  private handleAuthError(err: HttpErrorResponse): Observable<any> {
    const errorPayload = err?.error?.payload;
    const errorCode = err?.error?.code;
    const errorMessage = err?.error?.message?.toLowerCase() || '';

    // -------------------------
    // IMPORTANT: Check if this is a SIGNUP/REGISTRATION endpoint
    // -------------------------
    const isSignupRequest = err.url?.includes('/signup') || 
                           err.url?.includes('/register') ||
                           err.url?.includes('SIGNUP');

    // -------------------------
    // Handle 401 / 403 / Token Expired - BUT NOT for signup errors
    // -------------------------
    const isTokenError = (
      errorMessage.includes('token expired') ||
      errorMessage.includes('invalid token') ||
      errorMessage.includes('token is expired') ||
      errorMessage.includes('jwt expired') ||
      errorMessage.includes('unauthorized access') ||
      errorMessage.includes('different datasource') ||
      errorMessage.includes('datasource')
    );

    if (
      (err.status === 401 || errorCode === 'ERROR_CODE_UNAUTHORIZED') &&
      (isTokenError || errorMessage.includes('datasource')) &&
      !isSignupRequest // Don't treat signup errors as session expiry
    ) {
      if (!AuthInterceptor.isSessionExpired) {
        AuthInterceptor.isSessionExpired = true;

        this.clearStorage();
        this.sharedService.hideLoader();

        const message = errorMessage.includes('datasource')
          ? 'Invalid authentication token. Please login again.'
          : 'Your session has expired. Please login again.';

        this.sharedService.createNotification('error', message);

        setTimeout(() => {
          this.router.navigate(['/auth/login']).then(() => {
            AuthInterceptor.isSessionExpired = false;
          });
        }, 1500);
      }

      return throwError(() => err);
    }

    // -------------------------
    // Handle 403 separately (Access Forbidden)
    // -------------------------
    if (err.status === 403 && !isSignupRequest) {
      // Check if it's an access denied scenario that needs redirection
      if (errorCode === 'ERROR_CODE_ACCESS_DENIED') {
        this.handleAccessDenied(errorPayload);
        return throwError(() => err);
      }

      // Otherwise, it might be a token issue
      if (isTokenError && !AuthInterceptor.isSessionExpired) {
        AuthInterceptor.isSessionExpired = true;
        this.clearStorage();
        this.sharedService.hideLoader();

        this.sharedService.createNotification(
          'error',
          'Your session has expired. Please login again.'
        );

        setTimeout(() => {
          this.router.navigate(['/auth/login']).then(() => {
            AuthInterceptor.isSessionExpired = false;
          });
        }, 1500);

        return throwError(() => err);
      }
    }

    // -------------------------
    // Onboarding-related redirects
    // -------------------------
    if (errorCode === 'ERROR_CODE_ACCESS_DENIED') {
      this.handleAccessDenied(errorPayload);
      return throwError(() => err);
    }

    // -------------------------
    // Handle Fatal Error
    // -------------------------
    if (errorCode === 'ERROR_FATAL') {
      this.sharedService.hideLoader();
      return throwError(() => err);
    }

    // -------------------------
    // Handle generic 401 errors (not caught by token error check above)
    // -------------------------
    if (err.status === 401 && !isSignupRequest && !AuthInterceptor.isSessionExpired) {
      AuthInterceptor.isSessionExpired = true;
      this.clearStorage();
      this.sharedService.hideLoader();

      this.sharedService.createNotification(
        'error',
        err?.error?.message || 'Authentication failed. Please login again.'
      );

      setTimeout(() => {
        this.router.navigate(['/auth/login']).then(() => {
          AuthInterceptor.isSessionExpired = false;
        });
      }, 1500);

      return throwError(() => err);
    }

    // -------------------------
    // Other errors - Let the component handle them
    // -------------------------
    if (!isSignupRequest) {
      this.sharedService.hideLoader();
    }

    return throwError(() => err);
  }

  private handleAccessDenied(errorPayload: any): void {
    this.sharedService.hideLoader();

    if (errorPayload) {
      if (
        errorPayload.company_status === "user don't have a registered company"
      ) {
        this.sharedService.createNotification(
          'info',
          'Please complete your company setup'
        );
        setTimeout(() => this.router.navigate(['/auth/setup-company']), 2000);
      } else if (
        errorPayload.subscription_status === 'user in not subscribed yet'
      ) {
        this.sharedService.createNotification(
          'info',
          'Please subscribe to a plan'
        );
        setTimeout(() => this.router.navigate(['/auth/choose-plan']), 2000);
      } else if (
        errorPayload.verification_status ===
        'user need to verify there account using top'
      ) {
        this.sharedService.createNotification(
          'info',
          'Please verify your account using OTP'
        );
        setTimeout(() => this.router.navigate(['/auth/verify-otp']), 2000);
      } else {
        this.sharedService.createNotification(
          'error',
          'Access denied. Please contact support.'
        );
        setTimeout(() => this.router.navigate(['/auth/login']), 2000);
      }
    }
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip interceptor for AI API calls (they have their own authentication)
    if (req.url.includes('ai.luqta.io')) {
      return next.handle(req).pipe(
        catchError((x) => throwError(() => x)) // Don't trigger session expiry for AI API errors
      );
    }

    // Skip interceptor for ChatBolt API calls (uses x-api-key auth)
    if (req.url.includes('/api/v1/api-builder/')) {
      return next.handle(req).pipe(
        catchError((x) => throwError(() => x))
      );
    }
    if (req.url.includes('/api/v1/ai-endpoints/')) {
      return next.handle(req).pipe(
        catchError((x) => throwError(() => x))
      );
    }

    const token = localStorage.getItem('jwtToken')!;
    this.authReq = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'X-Data-Source': environment.dataSource,
      },
    });

    return next.handle(this.authReq).pipe(
      catchError((x) => this.handleAuthError(x))
    );
  }

  clearStorage() {
    localStorage.clear();
    sessionStorage.clear();
  }
}
